import React from 'react';

import create from 'zustand';
import { TransitionGroup } from 'react-transition-group';

import { cn } from 'domain/core';
import { DivProps, Transition } from 'components';

export type AppShellStore = {
	/**
	 * Mobile navbar opened
	 */
	navbarOpen: boolean;
	/**
	 * Side menu opened
	 */
	sideMenuOpen: boolean;
	/**
	 * Is side menu fixed?
	 */
	sideMenuFixed: boolean;
	/**
	 * Set side menu position
	 * @param sideMenuFixed
	 */
	setSideMenuFixed(sideMenuFixed: boolean): void;
	/**
	 * Open/close side menu
	 * @param open
	 */
	setSideMenuOpen(open: boolean): void;
	/**
	 * Open/close mobile navbar
	 */
	toggleNavbar: VoidFunction;
};

/**
 * App core store. Controls app layout as open/close side menu
 */
export const useAppShellStore = create<AppShellStore>((set) => ({
	navbarOpen: false,
	sideMenuOpen: true,
	sideMenuFixed: false,
	setSideMenuFixed(sideMenuFixed) {
		set({ sideMenuFixed });
	},
	setSideMenuOpen: (open: boolean) => set({ sideMenuOpen: open }),
	toggleNavbar: () => set((store) => ({ navbarOpen: !store.navbarOpen })),
}));

interface AppShellProps extends React.PropsWithChildren {
	className?: string;
}
/**
 * Main app layout component
 * @param props @see AppShellProps
 * @returns
 */
const AppShell = (props: AppShellProps) => {
	return (
		<div
			className={cn(
				'app-shell',
				'h-full w-full',
				'overflow-hidden',
				props.className
			)}>
			{props.children}
		</div>
	);
};

interface HeaderProps extends React.PropsWithChildren {
	className?: string;
}
/**
 * Header container
 * @param props
 * @returns
 */
const Header = (props: HeaderProps) => {
	return (
		<header
			className={cn(
				'app-shell-header',
				'h-[60px]',
				'flex flex-row items-center justify-start',
				'fixed top-0 left-0 z-50 w-full',
				props.className
			)}>
			{props.children}
		</header>
	);
};

interface SideMenuProps extends React.PropsWithChildren {
	className?: string;
}
/**
 * Side menu container
 * @param props
 * @returns
 */
const SideMenu = (props: SideMenuProps) => {
	const { sideMenuFixed } = useAppShellStore();

	return (
		<div
			className={cn(
				'app-shell-side-menu',
				'fixed top-12 left-0 z-50 h-full',
				'w-auto md:max-w-xs',
				props.className
			)}>
			{props.children}
		</div>
	);
};

interface ContentProps extends React.PropsWithChildren {
	className?: string;
}
/**
 * App content container
 * @param props
 * @returns
 */
const Content = (props: ContentProps) => {
	const { sideMenuFixed, sideMenuOpen } = useAppShellStore();

	return (
		<div
			role='main'
			className={cn(
				'app-shell-content',
				'relative top-12 z-10 box-border h-[calc(100%-4rem)] w-full overflow-y-auto overflow-x-hidden',
				sideMenuFixed
					? 'left-16 md:w-[calc(100%-5rem)]'
					: cn(sideMenuOpen ? '' : 'md:left-16 md:w-[calc(100%-4rem)]'),
				props.className
			)}>
			{props.children}
		</div>
	);
};

/**
 * Modals portal container. Used to render modals wrapped with Modal.Portal
 * @returns
 */
const ModalContainer = () => {
	return <div id='app-shell-modal-portal' />;
};

type AppAlert = {
	alert: React.ReactNode;
	duration?: number;
};

type AlertManagerState = {
	alerts: AppAlert[];
	pushAlert(alert: AppAlert): void;
	removeAlert(symbol: symbol): void;
	shift: VoidFunction;
	pop: VoidFunction;
	clean: VoidFunction;
};

export const useAlertState = create<AlertManagerState>((set, get) => ({
	alerts: [],
	pushAlert(alert) {
		if (
			!get().alerts.find(
				(a) =>
					(a.alert as any).props.symbol === (alert.alert as any).props.symbol
			)
		) {
			const newAlerts = get().alerts.concat(alert);
			set({ alerts: newAlerts });
		}
	},
	removeAlert(symbol) {
		if (symbol) {
			const newAlerts = get().alerts.filter(
				(a) => (a.alert as any).props.symbol !== symbol
			);
			set({ alerts: newAlerts });
		}
	},
	shift() {
		const newAlerts = get().alerts.slice();
		newAlerts.shift();
		set({ alerts: newAlerts });
	},
	pop() {
		const newAlerts = get().alerts.slice();
		newAlerts.pop();
		set({ alerts: newAlerts });
	},
	clean() {
		set({ alerts: [] });
	},
}));

type AlertManagerProps = DivProps;
export const AlertManager = React.forwardRef(
	({ className, children, ...props }: AlertManagerProps, ref: any) => {
		const [alerts, removeAlert] = useAlertState((state) => [
			state.alerts.slice().reverse(),
			state.removeAlert,
		]);

		const setupTimeout = React.useCallback((alert: AppAlert) => {
			const time = alert.duration ?? 10000;
			const symbol = (alert.alert as any).props.symbol;
			setTimeout(() => {
				removeAlert(symbol);
			}, time);
		}, []);

		return (
			<TransitionGroup
				className={cn(
					'fixed bottom-8 right-8 z-50',
					'flex h-auto w-auto flex-col items-center justify-end gap-2'
				)}>
				{alerts.map((alert) => {
					setupTimeout(alert);
					const symbol = (alert.alert as any).props.symbol;
					return (
						<Transition.Appear key={symbol.toString()} timeout={150}>
							{alert.alert}
						</Transition.Appear>
					);
				})}
			</TransitionGroup>
		);
	}
);

AppShell.Header = Header;
AppShell.SideMenu = SideMenu;
AppShell.Content = Content;
AppShell.ModalContainer = ModalContainer;
AppShell.AlertManager = AlertManager;

AppShell.displayName = 'AppShell';

export { AppShell };
